<template>
    <div>
        <div class="flex flex-wrap">
            <!-- Picture -->
            <div class="pr-4 w-1/3 xl:w-1/4">
                <h5 class="text-lg font-bold">Featured Image</h5>
                <div class="rounded-md relative w-full max-h-max items-center">
                    <!-- Image exists -->
                    <div v-if="form.image != null" class="relative">
                        <img v-if="!addingImage" :src="'/storage/' + form.image" alt="ProductImage"
                            class="h-auto w-full self-center"
                        />

                        <img v-show="imageURL" :src="imageURL" alt="ImagePreview"
                            class="h-auto w-full self-center rounded-lg shadow-xl shadow-black"
                        />

                        <div v-show="!showRemoveImage" class="bg-white rounded-md border border-gray p-2 absolute bottom-2 right-2 z-10">
                            <img src="/images/icons/trash.svg" width="20" height="20" alt="DeleteImage"
                                class="cursor-pointer" @click="showRemoveImage = true"
                            />
                        </div>
                    </div>

                    <!-- Remove Image Div -->
                    <div v-show="showRemoveImage" class="z-30 absolute top-8 bg-white text-center rounded-lg border border-gray shadow-lg">
                        <h2 class="bg-red-400 text-white px-4 py-2 rounded-t-lg">Delete Current Image</h2>
                        <div class="my-6">Are you sure?</div>

                        <div class="mt-4 mb-2 w-full">
                            <jet-button @click.stop.prevent="removeCurrentImage" class="mr-3">
                                Yes
                            </jet-button>

                            <jet-tertiary-button @click.prevent="showRemoveImage = false;" class="ml-3">
                                No
                            </jet-tertiary-button>
                        </div>
                    </div>

                    <!-- No image - upload icon/preview -->
                    <div v-show="form.image == null" class="flex items-center justify-center w-full">
                        <label for="dropzone-file-featured" class="flex flex-col items-center justify-center w-full h-1/3 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                            <div class="flex flex-col items-center justify-center pt-5 pb-2">
                                <svg aria-hidden="true" class="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                                <p class="mb-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-semibold">Click to upload</span></p>
                            </div>
                            <jet-input v-on="form.image == null ? { click: addImage(null, 'image') } : {} " id="dropzone-file-featured" class="hidden" type="file" ref="image" accept="image/*" @change="inputImage($event, 'image')" />
                        </label>
                    </div> 
                </div>

                <!-- Gallery images -->
                <div>
                    <h5 class="text-lg font-bold">Gallery</h5>
                    <div class="rounded-md relative w-full max-h-max items-center">
                        <div class="d-flex flex-wrap">
                            <image-upload
                                v-for="(image, index) in form.images"
                                :key="image.id"
                                :index="index"
                                :image="image"
                                :width="200"
                                :height="200"
                                @reset="reset"
                                class="mr-2"
                            />
                        </div>

                        <div class="flex items-center justify-center w-full">
                            <label for="dropzone-file" class="flex flex-col items-center justify-center w-full h-1/3 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                                <div class="flex flex-col items-center justify-center pt-5 pb-2">
                                    <svg aria-hidden="true" 
                                            class="w-10 h-10 mb-3 text-gray-400" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24" 
                                            xmlns="http://www.w3.org/2000/svg">
                                        <path stroke-linecap="round" 
                                                stroke-linejoin="round" 
                                                stroke-width="2" 
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12">
                                        </path>
                                    </svg>
                                    <p class="mb-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-semibold">Click to upload</span></p>
                                </div>
                                <jet-input v-on="form.images.length === 0 ? { click: addImages } : {} " 
                                        id="dropzone-file" 
                                        class="hidden" 
                                        type="file" 
                                        ref="images" 
                                        accept="image/*" 
                                        @change="inputImage($event, 'images')" />
                            </label>
                        </div>

                        <jet-input-error :message="$page.props.errors.images" class="mt-1" />
                        <jet-input-error :message="imageErrorMsg" class="mt-1" />
                    </div>
                </div>
            </div>

            <!-- Info -->
            <div class="pl-4 pr-8 w-2/3 xl:w-1/2">
                <h1 class="h-8">Details</h1>

                <div class="mt-6">
                    <div class="flex justify-between h-8">
                        <div>
                            <toggle class="block" id="enabled" v-model="form.enabled" />
                            <jet-label for="enabled" value="Active" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="is_wholesale" v-model="form.is_wholesale" />
                            <jet-label for="is_wholesale" value="Wholesale only" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="hot-buy" v-model="form.hot_buy" />
                            <jet-label for="hot-buy" value="Hot Buy" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="local" v-model="form.local" />
                            <jet-label for="local" value="Local" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="special_order" v-model="form.special_order" />
                            <jet-label for="special_order" value="Wholesale Special" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="allow_partial_quantity" v-model="form.allow_partial_quantity" />
                            <jet-label for="allow_partial_quantity" value="Partial Qty" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="allow_oversell" v-model="form.allow_oversell"/>
                            <jet-label for="allow_oversell" value="Oversell" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="processed" v-model="form.processed"/>
                            <jet-label for="processed" value="Processed" class="ml-2 inline-block" />
                        </div>
                    </div>
                </div>

                <div class="mt-6">
                    <div v-if="!form.is_wholesale" class="flex gap-x-12">
                        <div>
                            <toggle class="block" id="new" v-model="form.new" />
                            <jet-label for="new" value="New" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="gift-card" v-model="form.gift_card" />
                            <jet-label for="gift-card" value="Gift Card" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="organic" v-model="form.organic" />
                            <jet-label for="organic" value="Organic" class="ml-2 inline-block" />
                        </div>

                        <div>
                            <toggle class="block" id="retail_special" v-model="form.retail_special" />
                            <jet-label for="retail_special" value="Retail Special" class="ml-2 inline-block" />
                        </div>
                    </div>
                </div>

                <!-- Business Central Info -->
                <div class="mt-6 p-2 border rounded-md bg-gray-100">
                    <div class="flex justify-between gap-x-4">
                        <h5 class="text-lg font-bold mb-2">
                            Business Central Info
                        </h5>

                        <div v-if="bcFieldsDisabled" class="text-xs italic font-bold">
                            <div class="flex gap-x-2 items-end">
                                <div>Product Synced w BC</div>
                                <img src="/images/icons/green_check.svg" width="20" height="20" />
                            </div>
                        </div>
                        <jet-button v-else @click="itemSync" :disabled="form.processing" >
                            BC Sync
                        </jet-button>
                    </div>
    
                    <div class="mb-2">
                        <toggle class="inline-block" id="master_product" v-model="form.is_master_product" :disabled="bcFieldsDisabled" />
                        <jet-label for="master_product" value="Master Product" class="ml-2 inline-block" />
                    </div>

                    <div class="mb-2">
                        <jet-label for="unit_of_measure_id" value="Unit of Measure" class="inline-block" />
                        <div class="flex">
                            <div class="align-bottom contents">
                                <multiselect class="multi-custom multi-custom-append"
                                    v-model="form.unit_of_measure_id"
                                    :options="unitOfMeasureOptions"
                                    valueProp="id"
                                    trackBy="name"
                                    label="name"
                                    :searchable="true"
                                    placeholder="Unit of Measure"
                                    :disabled="bcFieldsDisabled"
                                >
                                    <template v-slot:option="{ option }">
                                        <span>{{ option.name }}</span>
                                    </template>
                                </multiselect>
                            </div>

                            <button type="button" class="inline-block rounded-md rounded-l-none whitespace-nowrap" @click="toggleUnitOfMeasureManagement">
                                <img src="/images/icons/cog.svg" width="16" height="16">
                            </button>
                        </div>
                        <jet-input-error :message="$page.props.errors.unit_of_measure_id" class="mt-1" />
                    </div>

                    <div v-if="!form.is_master_product">
                        <div class="mb-2">
                            <jet-label for="related_master_product_id" value="Related Master Product" />
                            <div class="flex">
                                <div class="align-bottom contents">
                                    <multiselect class="multi-custom"
                                        v-model="form.related_master_product_id"
                                        :options="masterProductOptions"
                                        valueProp="id"
                                        trackBy="title"
                                        labe="label"
                                        :searchable="true"
                                        placeholder="Master Product"
                                        :disabled="bcFieldsDisabled"
                                    >
                                        <template v-slot:option="{ option }">
                                            <span>
                                                {{ option.id }} - {{ option.title }}
                                                <template v-if="option.description"> - {{ option.description }}</template>
                                                <template v-if="option.size"> - {{ option.size }}</template>
                                            </span>
                                        </template>
                                    </multiselect>
                                </div>
                            </div>
                            <jet-input-error :message="$page.props.errors.related_master_product_id" class="mt-1" />
                        </div>
    
                        <div class="mb-2">
                            <jet-label for="conversion_factor" value="Conversion Factor" />
                            <jet-input type="number" class="block w-full" step="0.00001" min="0.00001" v-model="form.conversion_factor" :disabled="bcFieldsDisabled"/>
                            <div class="text-xs">The number of this product that can be taken out of the related master product.</div>
                            <jet-input-error :message="$page.props.errors.conversion_factor" class="mt-1" />
                        </div>
                    </div>
                </div>

                <div class="inline-block w-full align-top">
                    <jet-label for="title" value="Name" class="mt-6"/>
                    <jet-input type="text" class="mt-2 block w-full" v-model="form.title" ref="title" />
                    <jet-input-error :message="$page.props.errors.title" class="mt-1" />
                </div>

                <template v-if="!form.is_wholesale">
                    <!-- product brand -->
                    <div class="mt-4">
                        <jet-label value="Brand" for="item" />
                        <div class="flex h-8">
                            <select
                                v-if="!is_new_brand"
                                v-model="form.brand_id"
                                name="brand"
                                class="rounded-r-none text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm"
                            >
                                <option value=""></option>
                                <option
                                    :selected="form.brand_id === brand.id"
                                    :value="brand.id"
                                    v-for="brand in brands"
                                    :key="brand.id"
                                    v-text="brand.title"
                                ></option>
                            </select>
                            <jet-input v-model="form.brand_new" type="text" name="category" class="block w-full rounded-r-none h-8" placeholder="Add Brand" v-else />
                            <jet-input-error :message="$page.props.errors.brand_new" class="mt-1" />

                            <a  href="#"
                                id="basic-addon2"
                                @click.prevent="is_new_brand = !is_new_brand"
                                class="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border border-l-0 border-gray-300 rounded-r dark:bg-gray-600 dark:text-gray-400 dark:border-gray-600"
                            >
                                <span v-if="is_new_brand">x</span>
                                <span v-else>+</span>
                            </a>
                        </div>
                    </div>
                    <!-- / product brand -->

                     <div class="mt-4">
                        <jet-label for="store_excludes" value="Store Excludes" />
                        <multiselect
                            class="multi-custom multi-custom-append"
                            v-model="form.store_excludes"
                            mode="tags"
                            valueProp="id"
                            label="name"
                            placeholder="Store Excludes"
                            :close-on-select="false"
                            :searchable="false"
                            :create-option="true"
                            :options="location"
                        />
                    </div>
                    <!-- / store excludes -->
                </template>

                <div class="inline-block w-full mt-4 align-top">
                    <jet-label for="description" value="Description" />
                    <jet-input type="text" class="block w-full" v-model="form.description" />
                    <jet-input-error :message="$page.props.errors.description" class="mt-1" />
                </div>

                <div class="inline-block w-full mt-4 align-top">
                    <jet-label for="category" value="Category" />
                    <div class="flex">
                        <div class="align-bottom contents">
                            <multiselect class="multi-custom multi-custom-append"
                                v-model="form.category_id"
                                :options="categoryOptions"
                                valueProp="id"
                                trackBy="slug"
                                label="title"
                                :searchable="true"
                                placeholder="Category"
                            >
                                <template v-slot:option="{ option }">
                                    <span :class="{ 'ml-5' : option.parent_id != null }">{{ option.title }}</span>
                                </template>
                            </multiselect>
                        </div>

                        <button type="button" class="inline-block w-16" @click.stop="openCategoryModal">
                            {{ form.category_id == null ? 'Add' : 'Edit'}}
                        </button>

                        <button type="button" class="inline-block rounded-md rounded-l-none whitespace-nowrap" @click="openCategoryOrderModal">
                            Reorder
                        </button>
                    </div>
                    <jet-input-error :message="$page.props.errors.category_id" class="mt-1" />
                </div>

                <div class="inline-block w-full mt-4 align-top">
                    <jet-label for="purchaser_id" value="Purchaser" />
                    <multiselect
                        class="multi-custom"
                        v-model="form.purchaser_id"
                        valueProp="id"
                        label="name"
                        :close-on-select="true"
                        :options="purchasers"
                        track-by="id"
                    />
                </div>

                <div>
                    <div class="inline-block w-1/2 pr-2 mt-4 align-top">
                        <jet-label for="zone" value="Wholesale Zone" />
                        <jet-input type="number" class="block w-full" step="1" min="0" max="99" v-model="form.zone" />
                        <jet-input-error :message="$page.props.errors.zone" class="mt-1" />
                    </div>

                    <div class="inline-block w-1/2 pl-2 mt-4 align-top">
                        <jet-label for="subzone" value="Wholesale Subzone" />
                        <jet-input type="number" class="block w-full" step="1" min="0" max="99" v-model="form.subzone" />
                        <jet-input-error :message="$page.props.errors.subzone" class="mt-1" />
                    </div>
                </div>

                <div v-if="!form.is_wholesale">
                    <div class="inline-block w-1/2 pr-2 mt-4 align-top">
                        <jet-label for="retail_zone" value="Retail Zone" />
                        <jet-input type="number" class="block w-full" step="1" min="0" max="99" v-model="form.retail_zone" />
                        <jet-input-error :message="$page.props.errors.retail_zone" class="mt-1" />
                    </div>

                    <div class="inline-block w-1/2 pl-2 mt-4 align-top">
                        <jet-label for="retail_subzone" value="Retail Subzone" />
                        <jet-input type="number" class="block w-full" step="1" min="0" max="99" v-model="form.retail_subzone" />
                        <jet-input-error :message="$page.props.errors.retail_subzone" class="mt-1" />
                    </div>
                </div>

                <div>
                    <div class="inline-block w-1/2 pr-2 mt-4 align-top">
                        <jet-label for="school_zone" value="School Zone" />
                        <jet-input type="number" class="block w-full" step="1" min="0" max="99" v-model="form.school_zone" />
                        <jet-input-error :message="$page.props.errors.school_zone" class="mt-1" />
                    </div>

                    <div class="inline-block w-1/2 pl-2 mt-4 align-top">
                        <jet-label for="school_subzone" value="School Subzone" />
                        <jet-input type="number" class="block w-full" step="1" min="0" max="99" v-model="form.school_subzone" />
                        <jet-input-error :message="$page.props.errors.school_subzone" class="mt-1" />
                    </div>
                </div>

                <div>
                    <div class="inline-block w-1/2 pr-2 mt-4 align-top">
                        <jet-label for="size" value="UofM" />
                        <jet-input type="text" class="block w-full" v-model="form.size" />
                        <jet-input-error :message="$page.props.errors.size" class="mt-1" />
                    </div>

                    <div class="inline-block w-1/2 pl-2 mt-4 align-top">
                        <jet-label for="stock" value="Qty on Hand" />
                        <jet-input
                            type="number"
                            step="any"
                            class="block w-full"
                            :min="-9999"
                            :max="9999"
                            v-model="form.stock"
                            disabled
                        />
                        <jet-input-error :message="$page.props.errors.stock" class="mt-1" />
                    </div>
                </div>

                <template v-if="!form.is_wholesale">
                    <div>
                        <div class="inline-block w-1/2 pr-2 mt-4">
                            <jet-label for="item" value="Sort" />
                            <select v-model="form.sort_by" name="sort_by" class="block w-full">
                                <option :value="null">A-Z</option>
                                <option v-for="int in 24" :key="int" :value="int">{{ int }}</option>
                            </select>
                        </div>

                        <div class="inline-block w-1/2 pl-2 mt-4 align-top">
                            <jet-label for="max-order-qty" value="Max / Order" />
                            <jet-input type="number" step="any" :min="1" class="block w-full" placeholder="Unlimited" v-model="form.max_order_qty" />
                            <jet-input-error :message="$page.props.errors.max_order_qty" class="mt-1" />
                        </div>
                    </div>
                </template>
                
                <div>
                    <div class="inline-block mt-4 align-top w-full">
                        <jet-label for="long_description" value="Long Description" />
                        <quill-editor v-model:content="form.long_description" contentType="html" theme="snow" class="h-32" />
                        <jet-input-error :message="$page.props.errors.long_description" class="mt-1" />
                    </div>
                </div>

                <div class="inline-block w-full mt-4 align-top">
                    <jet-label for="custom_sold_out_message" value="Custom Sold Out Message" />
                    <jet-input type="text" class="block w-full" v-model="form.custom_sold_out_message" placeholder='Leave blank to use the default "Sold out, coming soon!"' />
                    <jet-input-error :message="$page.props.errors.custom_sold_out_message" class="mt-1" />
                </div>

                <div class="flex justify-between mt-4 w-full">
                    <jet-label for="picksheet_highlights">Picksheet highlights</jet-label>
                </div>
                <textarea
                    v-model="form.picksheet_highlights"
                    class="mt-1 block rounded w-full h-42"
                    placeholder="Picksheet highlights, seperated by commas"
                    name="picksheet_highlights"
                    id="picksheet_highlights"
                >
                </textarea>

                <div class="mb-32">
                    <div class="flex justify-between mt-4 w-full">
                        <jet-label for="contents">Contents</jet-label>
                    </div>

                    <div class="flex justify-between mb-2 w-full" v-show="hasSubproducts()">
                        <div class="flex items-center">
                            <jet-input
                                type="checkbox"
                                class="h-5 w-5"
                                id="require_subitem_quantities"
                                name="require_subitem_quantities"
                                :checked="form.require_subitem_quantities"
                                @change="form.require_subitem_quantities = !form.require_subitem_quantities">
                            </jet-input>

                            <label class="custom-control-label ml-2" for="require_subitem_quantities">Require Contents' Quantities</label>
                        </div>

                        <div class="flex items-center">
                            <jet-input
                                type="checkbox"
                                class="h-5 w-5"
                                id="require_subitem_quantities"
                                name="require_subitem_quantities"
                                :checked="form.include_subitems_on_picksheet"
                                @change="form.include_subitems_on_picksheet = !form.include_subitems_on_picksheet">
                            </jet-input>

                            <label class="custom-control-label ml-2" for="require_subitem_quantities">Include Contents On Picksheet</label>
                        </div>
                    </div>

                    <textarea
                        v-show="!hasSubproducts()"
                        v-model="form.contents"
                        class="mt-2 block rounded w-full h-42"
                        id="contents"
                        ref="contents"
                        placeholder="Description of contents (optional)..."
                        maxlength="750"
                        name="contents"
                    >
                    </textarea>

                    <sub-products
                        :products="productsForContentsSelect"
                        :subproducts="form.subproducts"
                        :categories="categories"
                    >
                    </sub-products>
                </div>
            </div>

            <!-- Pricing -->
            <div v-if="hasFinanceRole" class="xl:pl-8 mt-8 xl:mt-0 w-full xl:w-1/4 xl:border-l border-gray">
                <h1>Pricing</h1>

                <div class="flex flex-col xl:flex-row gap-x-4 mt-6">
                    <div>
                        <toggle id="taxable" v-model="form.taxable" />
                        <jet-label for="taxable" value="Taxable" class="ml-2 inline-block" />
                    </div>
                    <div v-if="can_show_sfd_product_toggle">
                        <toggle id="sfd_product" v-model="form.sfd_product" />
                        <jet-label for="sfd_product" value="SFD Product" class="ml-2 inline-block" />
                    </div>
                </div>

                <div v-if="form.sfd_product" class="mt-[48px]">
                    {{ sfd_product_text }}
                </div>
                <div v-else>
                    <div class="my-3">
                        <jet-label for="cost" value="Cost" />
                        <jet-input type="number" class="mt-2 block w-full" step="0.01" v-model.number="form.cost" disabled />
                        <p v-show="hasSubproducts() && !form.is_wholesale">
                            <small>*Content total cost: ${{ formatNumber(subproductTotalCost()).toFixed(2) }}</small>
                        </p>
                        <jet-input-error :message="$page.props.errors.cost" class="mt-1" />
                    </div>

                    <div class="my-3">
                        <jet-label for="price" value="Price (MSRP)" />
                        <jet-input type="number" class="mt-2 block w-full" step="0.01" v-model.number="form.price" />
                        <p v-show="hasSubproducts() && !form.is_wholesale">
                            <small>*Content total price: ${{ formatNumber(subproductTotalPrice()).toFixed(2) }}</small>
                        </p>
                        <jet-input-error :message="$page.props.errors.price" class="mt-1" />
                    </div>

                    <div v-for="pricing in form.pricing" :key="pricing.id" class="my-3">
                        <template v-if="!form.is_wholesale || (form.is_wholesale && !pricing.pricing_level_title.includes('Retail'))">
                            <jet-label for="level_1" :value="pricing.pricing_level_title" />

                            <div  v-if="!pricing.pricing_level_title.includes('Retail') || !form.free_for_orders_over_enabled" class="flex gap-x-4 items-center">
                                <price-select
                                    :pricing="pricing"
                                    @price-level-update="handlePriceLevelUpdate"
                                />
                                <span v-if="pricing.modifier_type !== 'disabled'">
                                    {{ toCurrency(pricing.price) }}
                                </span>
                            </div>
                        </template>
                    </div>

                    <div v-if="!form.is_wholesale && !form.free_for_orders_over_enabled">
                        <jet-label for="" value="Retail price 2+" />
                        <div class="flex gap-x-4 items-center">
                            <div class="inline-flex justify-end">
                                <input
                                    id="price-2plus"
                                    v-model="form.price_2plus"
                                    type="number"
                                    step="any"
                                    class="rounded-l rounded-r-none "
                                    :disabled="type === '' || type === null || type === 'disabled'"
                                />

                                <select v-model="type" @change="modifyPriceTwoPlus" class="rounded-r rounded-l-none">
                                    <option value="fixed">$</option>
                                    <option value="disabled">X</option>
                                </select>
                            </div>
                            <span v-if="form.price_2plus > 0 && type !== 'disabled'">
                                {{ toCurrency(form.price_2plus) }}
                            </span>
                        </div>
                    </div>

                    <div v-if="!form.is_wholesale">
                        <div class="mt-2">
                            <toggle id="free_for_orders_over_enabled" v-model="form.free_for_orders_over_enabled" />
                            <jet-label for="free_for_orders_over_enabled" value="Free Based on Order Total" class="ml-2 inline-block" />
                        </div>

                        <div v-if="form.free_for_orders_over_enabled" class="mt-2">
                            <jet-label for="free_for_orders_over_amount" value="Free for orders of or over" />
                            <div class="flex">
                                <span class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l">$</span>
                                <input
                                    id="free_for_orders_over_amount"
                                    v-model="form.free_for_orders_over_amount"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    class="rounded-r rounded-l-none"
                                />
                            </div>
                            <jet-input-error :message="$page.props.errors.free_for_orders_over_amount" class="mt-1" />
                        </div>
                    </div>
                </div>
            </div>
            <!-- / Pricing -->
        </div>

        <jet-dialog-modal :show="showCategoryModal" @close="closeCategoryModal">
            <template #title>
                {{ form.category_id == null ? 'Add' : 'Edit'}} Category
            </template>
            <template #content>
                <div style="min-height: 30vh">
                    <form @submit.prevent="saveCategory">
                        <div class="mt-2 mb-4">
                            <jet-label for="title" value="Category Name" />
                            <jet-input type="text" class="form-input block w-full" v-model="formCategory.title" />
                            <jet-input-error :message="categoryErrors.title?.length ? categoryErrors.title[0] : ''" class="mt-1" />
                        </div>

                        <div v-if="categoryHasNoChildren" class="mb-4">
                            <jet-label for="parent_id" value="Parent Category" />
                            <multiselect class="multi-custom"
                                v-model="formCategory.parent_id"
                                :options="parentCategoryOptions"
                                valueProp="id"
                                trackBy="slug"
                                label="title"
                                :searchable="true"
                                @open="selectOpen = true"
                                @close="selectOpen = false"
                                placeholder="Choose Parent Category (optional)"
                            />
                            <jet-input-error :message="categoryErrors.parent_id?.length ? categoryErrors.parent_id[0] : ''" class="mt-1" />
                        </div>

                        <div class="rounded-md relative w-full max-h-max items-center">
                            <!-- Image exists -->
                            <div v-if="formCategory.image" class="relative">
                                <img v-if="typeof formCategory.image === 'string' && !categoryImageURL" :src="'/storage/' + formCategory.image" alt="ProductImage"
                                    class="h-auto w-full self-center"
                                />

                                <img v-show="categoryImageURL" :src="categoryImageURL" alt="ImagePreview"
                                    class="h-auto w-full self-center rounded-lg shadow-xl shadow-black"
                                />

                                <div 
                                    v-show="!categoryShowRemoveImage && (categoryImageURL || typeof formCategory.image === 'string')" 
                                    class="bg-white rounded-md border border-gray p-2 absolute bottom-2 right-2 z-10">
                                    <img src="/images/icons/trash.svg" width="20" height="20" alt="DeleteImage"
                                        class="cursor-pointer" @click="categoryShowRemoveImage = true"
                                    />
                                </div>
                            </div>

                            <!-- Remove Image Div -->
                            <div v-show="categoryShowRemoveImage" class="z-30 absolute top-8 left-1/2 bg-white text-center rounded-lg border border-gray shadow-lg" style="transform: translate(-50%, 0px)">
                                <h2 class="bg-red-400 text-white px-4 py-2 rounded-t-lg">Delete Current Image</h2>
                                <div class="my-6">Are you sure?</div>

                                <div class="mt-4 mb-2 w-full">
                                    <jet-button @click.stop.prevent="removeCurrentImage('category')" class="mr-3">
                                        Yes
                                    </jet-button>

                                    <jet-tertiary-button @click.prevent="categoryShowRemoveImage = false;" class="ml-3">
                                        No
                                    </jet-tertiary-button>
                                </div>
                            </div>

                            <!-- No image - upload icon/preview -->
                            <div v-show="!formCategory.image" class="flex items-center justify-center w-full">
                                <label for="dropzone-file-category-image" class="flex flex-col items-center justify-center w-full h-1/3 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                                    <div class="flex flex-col items-center justify-center pt-5 pb-2">
                                        <svg aria-hidden="true" class="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                                        <p class="mb-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-semibold">
                                            Click to upload
                                        </span></p>
                                    </div>
                                    <jet-input v-on="!formCategory.image ? { click: addImage('category', 'categoryImage') } : {} " id="dropzone-file-category-image" class="hidden" type="file" ref="categoryImage" accept="image/*" @change="inputImage($event, 'image', 'category')" />
                                </label>
                            </div>
                        </div>

                        <jet-input-error :message="categoryImageErrorMsg" class="mt-1" />

                        <input type="submit" ref="categorySubmit" class="hidden" />
                    </form>
                </div>
            </template>
            <template #footer>
                <div class="flex">
                    <div class="flex-grow text-left relative">
                        <div v-if="this.formCategory.id !== null">
                            <jet-danger-button @click="deleteCategory" :disabled="!categoryHasNoProducts || !categoryHasNoChildren">
                                Delete
                            </jet-danger-button>

                            <div class="text-xs text-gray-300 absolute -bottom-4" v-if="!categoryHasNoProducts">Cannot delete as there are products in this category.</div>
                            <div class="text-xs text-gray-300 absolute -bottom-4" v-else-if="!categoryHasNoChildren">Cannot delete as there are subcategories for this category.</div>
                        </div>
                    </div>
                    <div class="flex-shrink">
                        <jet-tertiary-button @click="closeCategoryModal">
                            Cancel
                        </jet-tertiary-button>

                        <jet-button class="ml-2" @click="$refs.categorySubmit.click()" :class="{ 'opacity-25': formCategory.processing }" :disabled="formCategory.processing">
                            Save
                        </jet-button>
                    </div>
                </div>
            </template>
        </jet-dialog-modal>

        <jet-dialog-modal  :show="showCategoryOrderModal" @close="closeCategoryOrderModal">
            <template #title>
                Reorder Categories
            </template>
            <template #content>
                <div class="mb-2">The order of the categories in the catalogue is determined by the order shown in the list. By default, new categories are added to the end of the list. Subcategories are always ordered alphabetically beneath their parent category.</div>
                <div class="mb-2">Please note, only categories with products will show in the cataglogue.</div>

                <div
                    v-for="category, index in formCategoryOrder.categories"
                    :key="category.id"
                    class="px-2 py-1 flex items-center gap-x-4"
                    :class="{
                        'bg-gray-100': index % 2 == 0,
                        'bg-gray-200': index % 2 != 0
                    }"
                >
                    <div class="flex-shrink">
                        <div v-if="index > 0" class="cursor-pointer hover:opacity-60" @click="moveUp(index)">
                            <img src="/images/icons/chevron-up.svg" width="16" height="16" alt="Up">
                        </div>
                        <div v-if="index < (formCategoryOrder.categories.length - 1)" class="cursor-pointer hover:opacity-60" @click="moveDown(index)">
                            <img src="/images/icons/chevron-down.svg" width="16" height="16" alt="Down">
                        </div>
                    </div>
                    <div class="flex-grow">
                        {{ category.title }}
                    </div>
                </div>

                <div v-if="categoryOrderUpdateError" class="mt-3 text-red-600">
                    The categories have been updated since you started.<br />
                    The list above has been refreshed - please try again.
                </div>
            </template>
            <template #footer>
                <jet-tertiary-button @click="closeCategoryOrderModal">
                    Cancel
                </jet-tertiary-button>

                <jet-button class="ml-2" @click="saveCategoryOrder" :class="{ 'opacity-25': formCategoryOrder.processing }" :disabled="formCategoryOrder.processing">
                    Save
                </jet-button>
            </template>
        </jet-dialog-modal>

        <jet-dialog-modal :show="showUnitOfMeasureManagement" @close="showUnitOfMeasureManagement=false">
            <template #title>
                Unit of Measure
            </template>

            <template #content>
                <div class="mb-4">
                    Manage all units of measure (uoms) that are used in Business Central item synchronization. As soon as you add a uom to this list, it will be added to Business Central. Please note that you cannot modify uoms once created and you must add a new one instead and delete the old one (if necessary).
                </div>
                <div v-if="unitOfMeasureSuccessMessage" class="text-green">{{ unitOfMeasureSuccessMessage }}</div>
                <table class="table w-full">
                    <tbody>
                        <tr v-for="(uom, index) in unit_of_measures" :key="index">
                            <td class="w-min">
                                <img src="/images/icons/green_check.svg" v-if="BCInfoFilled(uom) == 'no_error'" width="24" height="24" class="mx-auto" style="max-width: none !important;" />
                                <img src="/images/icons/x-red.svg" v-if="BCInfoFilled(uom) == 'error'" width="24" height="24" class="mx-auto" :title="errorMessageBC(uom.bc_error)" style="max-width: none !important;" />
                            </td>
                            <td class="w-full">
                                 {{ uom.name }}
                            </td>
                            <td class="w-min">
                                <jet-button @click="uomSync(uom.id)" :disabled="form.processing" style="white-space: nowrap;" >
                                    BC Sync
                                </jet-button>
                            </td>
                            <td class="w-min">
                                <jet-danger-button @click="deleteUnitOfMeasure(uom.id)" :disabled="unitOfMeasureSaving">
                                    Delete
                                </jet-danger-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </template>

            <template #footer>
                <div class="flex gap-x-4">
                    <jet-input type="text" class="w-full text-left" placeholder="New UOM" v-model="unitOfMeasureName" />

                    <jet-button class="ml-2" @click="saveUnitOfMeasure" :disabled="unitOfMeasureSaving">
                        Add
                    </jet-button>
                </div>
            </template>
        </jet-dialog-modal>
    </div>
</template>

<script>
import axios from 'axios';
import UseUserAuth from '@/Composables/UseUserAuth';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetLabel from '@/Jetstream/Label';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import PriceSelect from '@/Pages/Pricing/Partials/PriceSelect';
import ImageUpload from '@/Pages/Product/Partials/ImageUpload.vue';
import SubProducts from '@/Pages/Product/Partials/SubProducts.vue';
import { calculatePricingLevel } from '@/helper';
import { useForm, usePage } from '@inertiajs/inertia-vue3';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';
import { QuillEditor } from '@vueup/vue-quill';
import Compressor from 'compressorjs';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
    components: {
        SubProducts,
        JetButton,
        JetDialogModal,
        JetInput,
        JetInputError,
        JetLabel,
        JetTertiaryButton,
        JetDangerButton,
        PriceSelect,
        Multiselect,
        Toggle,
        QuillEditor,
        ImageUpload
    },

    props: [
        'product',
        'location',
        'productsForContentsSelect',
        'brands',
        'categories',
        'master_products',
        'unit_of_measures',
        'form',
        'sfd_product_count',
        'max_sfd_product_count',
        'purchasers'
    ],

    setup() {
        const formCategory = useForm({
            id: '',
            parent_id: '',
            slug: '',
            title: '',
            image: '',
            products: [],
        })

        const formCategoryOrder = useForm({
            categories: [],
        })

        const { hasFinanceRole } = UseUserAuth(usePage().props.value.user);

        return { formCategory, formCategoryOrder, hasFinanceRole }
    },

    data() {
        return {
            editorOptions: {
                theme: 'snow',
            },

            imageType: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
            showRemoveImage: false,
            addingImage: false,
            imageURL: null,
            imageErrorMsg: null,
            categoryShowRemoveImage: false,
            categoryAddingImage: false,
            categoryImageURL: null,
            categoryImageErrorMsg: null,

            showCategoryModal: false,
            showCategoryOrderModal: false,
            selectOpen: false,
            categoryOptions: this.categories,
            unitOfMeasureOptions: this.unit_of_measures,
            unitOfMeasureSaving: false,
            unitOfMeasureSuccessMessage: null,
            showUnitOfMeasureManagement: false,
            unitOfMeasureName: null,
            categoryErrors: [],
            categoryOrderUpdateError: false,
            categoryOrderUpdateErrorTimeout: null,
            is_new_brand: false,
            type: 'disabled',
        }
    },

    methods: { 
        async uomSync(uom_id) {
            this.form.processing = true;
            try {
                const response = await axios.post('/business-central/unitOfMeasureSync', {
                    uom_id: uom_id
                });

                console.log(response.data.message);
            } catch (error) {
                console.error(error);
                
                if (error.response && error.response.data && error.response.data.message) {
                    console.error(error.response.data.message);
                } else {
                    console.error('An unknown error occurred.');
                }

                alert('An error occurred while syncing the unit of measure. Please check the eror logs.');
            } finally {
                this.form.processing = false;
            }
        },

        async itemSync() {
            this.form.processing = true;
            try {
                const response = await axios.post('/business-central/itemSync', {
                    product_id: this.form.id
                });

                console.log(response.data.message);
            } catch (error) {
                console.error(error);
                
                if (error.response && error.response.data && error.response.data.message) {
                    console.error(error.response.data.message);
                } else {
                    console.error('An unknown error occurred.');
                }

                alert('An error occurred while syncing the product. Please check the error logs.');
            } finally {
                this.form.processing = false;
            }
        },

        errorMessageBC(errorString) {
            try {
                // Check if the error string is XML
                if (errorString.startsWith('<error')) {
                    // Parse XML string
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(errorString, 'application/xml');
                    const code = xmlDoc.getElementsByTagName('code')[0]?.textContent;
                    const message = xmlDoc.getElementsByTagName('message')[0]?.textContent;
                    if (code && message) {
                        return `${code}. ${message}`;
                    }
                } else {
                    // Parse JSON string
                    const errorObj = JSON.parse(errorString);
                    if (errorObj.error && errorObj.error.message) {
                        return errorObj.error.message;
                    }
                }
            } catch (e) {
                console.error('Error parsing the error string:', e);
            }

            return 'An unknown error occurred';
        },

        BCInfoFilled (uom) {
            if (uom.bc_error)
                return 'error';
            else 
                return 'no_error';
        },

        modifyPriceTwoPlus() {
            if (this.type === 'fixed') {
                return 'Fixed Price';
            } else if (this.type === 'disabled') {
                this.form.price_2plus = null;
                return "Disabled";
            }
        },

        calculate () {
            let pricing = {
                pricing_level_id: this.pricing.pricing_level_id,
                price: null,
                modifier_type: this.type,
                modifier_amount: this.roundDecimals(this.amount),
            }

            this.$emit('priceLevelUpdate', pricing);
        },

        reset(index) {
            this.form.images.splice(index, 1);
        },

        resetFeaturedImage () {
            this.showRemoveImage = true
        },

        formatNumber(number) {
            return Math.round(number * 100) / 100;
        },

        hasSubproducts() {
            return this.form.subproducts.length > 0;
        },

        saveCategory () {
            const url = !this.formCategory.id ? route('category.create') : route('category.edit', this.formCategory.id);

            let formCategoryData = this.formCategory.data();
            let formData = new FormData();
            Object.keys(formCategoryData).forEach((key) => {
                if (formCategoryData[key] !== null
                    || formCategoryData[key] !== ''
                    || formCategoryData[key] !== undefined) {
                    formData.append(key, formCategoryData[key]);
                }
                else formData.append(key, '');
            });

            axios
                .post(url,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        }
                    })
                .then(response => {
                    this.categoryOptions = response.data;

                    if (!this.formCategory.id) {
                        this.form.category_id = response.data.find(c => c.title === this.formCategory.title)?.id;
                    }

                    this.closeCategoryModal();
                })
                .catch(error => {
                    if (error.response.data.errors) {
                        this.categoryErrors = error.response.data.errors;
                    }
                });
        },

        deleteCategory () {
            const url = route('category.delete', this.formCategory.id);

            axios
                .post(url)
                .then(response => {
                    this.categoryOptions = response.data;

                    this.form.category_id = null;

                    this.closeCategoryModal();
                })
                .catch(error => {
                    if (error.response.data.errors) {
                        this.categoryErrors = error.response.data.errors;
                    }
                });
        },

        openCategoryModal () {
            if(this.form.category_id != null)
            {
                let productCat = this.categoryOptions.find(i => i.id == this.form.category_id);

                this.formCategory.id = productCat.id ?? '';
                this.formCategory.parent_id = productCat.parent_id ?? '';
                this.formCategory.title = productCat.title ?? '';
                this.formCategory.slug = productCat.slug ?? '';
                this.formCategory.image = productCat.image ?? '';
                this.formCategory.products = productCat.products;
            }

            this.showCategoryModal = true;
        },

        closeCategoryModal () {
            this.categoryErrors = [];
            this.formCategory.reset();
            this.showCategoryModal = false;
        },

        saveCategoryOrder () {
            const url = route('category.update_order');

            axios
                .post(url, this.formCategoryOrder)
                .then(response => {
                    if (response.data.result == 'updated')
                    {
                        this.categoryOrderUpdateError = true;
                        this.categoryOptions = response.data.categories;
                        this.formCategoryOrder.categories = JSON.parse(JSON.stringify(this.parentCategoriesToReorder));

                        this.categoryOrderUpdateErrorTimeout = setTimeout(() => {
                            this.categoryOrderUpdateError = false;
                        }, 8 * 1000)
                    }
                    else if (response.data.result == 'success')
                    {
                        this.categoryOptions = response.data.categories;
                        this.closeCategoryOrderModal();
                    }
                })
                .catch(error => {
                    if (error.response.data.errors) {
                        this.categoryOrderErrors = error.response.data.errors;
                    }
                });
        },

        openCategoryOrderModal () {
            this.formCategoryOrder.categories = JSON.parse(JSON.stringify(this.parentCategoriesToReorder));
            this.categoryOrderUpdateError = false;
            if (this.categoryOrderUpdateErrorTimeout) clearTimeout(this.categoryOrderUpdateErrorTimeout);
            this.showCategoryOrderModal = true;
        },

        closeCategoryOrderModal () {
            this.categoryOrderUpdateError = false;
            if (this.categoryOrderUpdateErrorTimeout) clearTimeout(this.categoryOrderUpdateErrorTimeout);
            this.formCategoryOrder.reset();
            this.showCategoryOrderModal = false;
        },

        moveUp (index) {
            this.categoryOrderUpdateError = false;
            if (this.categoryOrderUpdateErrorTimeout) clearTimeout(this.categoryOrderUpdateErrorTimeout);

            this.formCategoryOrder.categories.splice(index - 1, 0, this.formCategoryOrder.categories.splice(index, 1)[0]);
        },

        moveDown (index) {
            this.categoryOrderUpdateError = false;
            if (this.categoryOrderUpdateErrorTimeout) clearTimeout(this.categoryOrderUpdateErrorTimeout);

            this.formCategoryOrder.categories.splice(index + 1, 0, this.formCategoryOrder.categories.splice(index, 1)[0]);
        },

        subproductTotalCost() {
            // returns sum of costs of all item in a gift basket. If any one is null, returns null
            if(this.hasSubproducts) {
                return this.form.subproducts.reduce(function(carry, subproduct) {
                    return carry !== null && subproduct.cost ? carry + (Number(subproduct.cost) * Number(subproduct.qty)) : null;
                }, 0);
            }

            return null;
        },

        subproductTotalPrice() {
            // returns sum of retail price of all items in a gift basket
            if(this.hasSubproducts) {
                return this.form.subproducts.reduce(function(carry, subproduct) {
                    return carry + (Number(subproduct.price) * Number(subproduct.qty));
                }, 0);
            }

            return null;
        },

        addImage (type = null, ref) {
            if (this.$refs[ref] != undefined) {
                if (type === 'category') this.categoryAddingImage = true;
                else this.addingImage = true;
            }
        },

        inputImage (event, el, type = null) {
            let image = event.target.files[0];

            if (this.imageType.includes(image.type)) {
                const imageURL = URL.createObjectURL(image);
                if (type === 'category') this.categoryImageErrorMsg = null;
                else this.imageErrorMsg = null;
                this.compressFile(image, el, imageURL, type);
                event.target.value = null;
            } else {
                if (type === 'category') this.categoryImageErrorMsg = "The image must be a file of type: jpeg, png, jpg, gif, svg.";
                else this.imageErrorMsg = "The image must be a file of type: jpeg, png, jpg, gif, svg.";
            }
        },

        compressFile (file, el, imageURL, type = null) {
            new Compressor(file, {
                quality: 0.6,
                maxWidth: 600, //height will automatically preserve aspect ratio

                success: (result) => {
                    let newFile = new File([result], file.name,{type:file.type, lastModified:new Date().getTime()});

                    if (el == 'images') {
                        this.form[el].push([newFile, imageURL]);
                    } else if (type === 'category') {
                        this.categoryImageURL = imageURL;
                        this.formCategory[el] = newFile;
                    } else {
                        this.imageURL = imageURL;
                        this.form[el] = newFile;
                    }
                },
                error: function (error) {
                    if (el == 'images') {
                        this.form[el].push([newFile, imageURL]);
                    } else if (type === 'category') {
                        this.categoryImageURL = imageURL;
                        this.formCategory[el] = newFile;
                    } else {
                        this.imageURL = imageURL;
                        this.form[el] = newFile;
                    }

                    console.log(error);
                },
            });
        },

        addImages () {
            this.$refs.images.$el.click();
        },

        removeCurrentImage (type = null) {
            if (type === 'category') {
                this.formCategory.clearErrors();
                this.formCategory.image = null;
                this.categoryImageURL = null;
                this.categoryAddingImage = false;
                this.categoryShowRemoveImage = false;
            }
            else {
                this.form.clearErrors();
                this.form.image = null;
                this.imageURL = null;
                this.addingImage = false;
                this.showRemoveImage = false;
            }
        },

        handleProductUpdate () {
            this.form.pricing.forEach(p => {
                p.price = this.calculatePricingLevel(p, this.form.cost, this.form.price);
            });
        },

        handlePriceLevelUpdate (payload) {
            let index = this.form.pricing.findIndex(pl => pl.pricing_level_id === payload.pricing_level_id);

            if (index !== -1) {
                payload.price = this.calculatePricingLevel(payload, this.form.cost, this.form.price);

                if (payload.modifier_type === '' || payload.modifier_type === null || payload.modifier_type === 'disabled') {
                    payload.modifier_amount = null;
                }

                this.form.pricing[index] = {
                    ...this.form.pricing[index],
                    ...payload,
                    product_id: this.form.id,
                };
            }
        },

        calculatePricingLevel (pricingLevel, cost, price) {
            const result = calculatePricingLevel(pricingLevel, cost, price);

            if (pricingLevel.pricing_level_title.includes('Retail') && pricingLevel.modifier_type === null) {
                return price;
            }

            if (pricingLevel.pricing_level_title.includes('Retail') && this.form.free_for_orders_over_enabled) {
                return 0;
            }

            return result !== null ? this.roundDecimals(result) : null;
        },

        // Unit Of Measure
        toggleUnitOfMeasureManagement() {
            this.showUnitOfMeasureManagement = !this.showUnitOfMeasureManagement;
        },

        deleteUnitOfMeasure(id) {
            this.unitOfMeasureSaving = true;
            axios.delete(route('unit_of_measure.destroy', id))
                .then(response => {
                    this.unitOfMeasureSaving = false;

                    let index = this.unit_of_measures.findIndex(item => item.id === id);
                    if (index !== -1) {
                        this.unit_of_measures.splice(index, 1);
                    }
                })
                .catch(error => {
                    this.unitOfMeasureSaving = false;
                    console.log('delete data ' + error.response.data);
                });
        },

        saveUnitOfMeasure() {
            this.unitOfMeasureSaving = true;
            axios.post(route('unit_of_measure.store'), {name: this.unitOfMeasureName})
                .then(response => {
                    this.unitOfMeasureSaving = false;
                    this.unitOfMeasureSuccessMessage = 'Unit of Measure successfully added!';

                    setTimeout(() => {
                        this.unitOfMeasureSuccessMessage = null;
                    }, 5000);
                    
                    this.unit_of_measures.push(response.data);
                    this.unitOfMeasureName = null;
                })
                .catch(error => {
                    this.unitOfMeasureSaving = false;
                    console.log('store data ' + error.response.data);
                });
        },
    },

    computed: {
        masterProductOptions () {
            return this.master_products.map(product => ({ ...product, label: product.id + ' - ' + product.title + (product.description ? (' - ' + product.description) : '') + (product.size ? (' - ' + product.size) : '') }))
        },

        bcFieldsDisabled () {
            return this.product.bc_id !== null && this.product.bc_error === null;
        },

        parentCategoryOptions () {
            return this.categoryOptions.filter(c => c.parent_id === null && c.id !== this.form.category_id);
        },

        parentCategoriesToReorder () {
            let parentCategoriesToReorder = this.categoryOptions.filter(c => c.parent_id === null);

            return parentCategoriesToReorder.sort((a, b) => a.order_index - b.order_index);
        },

        categoryHasNoChildren () {
            if (this.form.category_id === null) {
                return true;
            }

            return this.categoryOptions.filter(c => c.parent_id === this.form.category_id).length === 0 ? true : false;
        },

        categoryHasNoProducts () {
            if (this.form.category_id === null) {
                return true;
            }

            // Get category products (excluding current product)
            let category_products = this.formCategory.products.filter(p => p.id !== this.form.id)

            return category_products.length === 0 ? true : false;
        },

        sfd_product_text () {
            let sfd_product_number = this.form.sfd_product_number;
            if (this.form.id == null || this.form.sfd_product_number == null) sfd_product_number = this.sfd_product_count + 1;

            return `This product will use Column ${String.fromCharCode('A'.charCodeAt(0) + sfd_product_number)} when generating SFD orders from Excel.`;
        },

        can_show_sfd_product_toggle () {
            if (this.form.id == null)
                return this.sfd_product_count < this.max_sfd_product_count;

            return this.form.sfd_product || this.sfd_product_count < this.max_sfd_product_count;
        },
    },

    watch: {
        pricing: {
            immediate: true,
            deep: true,
            handler (newValue) {
                if (!newValue || newValue.modifier_type === null) {
                    return;
                } else {
                    this.type = newValue.modifier_type;
                    this.amount = this.type === 'fixed' ? newValue.price : newValue.modifier_amount;
                }
            }
        },

        'form.cost' () {
            this.handleProductUpdate();
        },

        'form.price' () {
            this.handleProductUpdate();
        },

        'form.retail_special' (newVal, oldVal) {
            if(oldVal == 1 && newVal == 0) {
                this.form.sort_by = null;
            }
        },

        'form.new' (newVal, oldVal) {
            if(oldVal == 1 && newVal == 0) {
                this.form.sort_by = null;
            }
        },
    },

    mounted () {
        if (this.form.id === null) {
            this.$refs.title.focus();
        }

        this.form.price_2plus > 0 ? this.type = 'fixed' : this.type = 'disabled';
    }
}
</script>

<style scoped>
    .multi-custom {
        min-height: 2.1rem;
    }
    .multi-custom-append {
        margin-right: -1px;
        @apply rounded-r-none !important;
    }
    .toggle-container.block {
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>

<style>
    .ql-container {
        height: 8rem;
    }
    input[id="price-2plus"] {
        @apply py-1 px-1;
        max-width: 75px;
        margin-right: -1px;
    }

    select:focus,
    input[id="price-2plus"]:focus {
        @apply z-10;
    }

    select {
        @apply text-sm pl-2 py-1 pr-8 border border-gray ring-green;
    }

    .price-select-sm input[id="price-2plus"],
    .price-select-sm select {
        @apply py-0;
    }
</style>
